import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { DollarSign, Plus, Edit2, Trash2, Clock, Calendar, Percent, Package, Tag } from "lucide-react";

const DynamicPricing = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const venueId = searchParams.get('venueId');
  
  const [venue, setVenue] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [venues, setVenues] = useState([]);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [showPackageDialog, setShowPackageDialog] = useState(false);
  
  const [newCoupon, setNewCoupon] = useState({
    code: '',
    discountType: 'percentage',
    discountValue: 0,
    minBookingAmount: 0,
    validFrom: new Date().toISOString().split('T')[0],
    validUntil: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
    usageLimit: null
  });
  
  const [newPackage, setNewPackage] = useState({
    name: '',
    description: '',
    bookingCount: 10,
    totalPrice: 0,
    validityDays: 90
  });

  const [dayPricing, setDayPricing] = useState([
    { day: 'Hafta İçi', basePrice: 0, timeSlots: [] },
    { day: 'Hafta Sonu', basePrice: 0, timeSlots: [] }
  ]);

  useEffect(() => {
    if (user?.role !== "venue_owner") {
      navigate("/");
      return;
    }
    fetchVenues();
  }, [user]);

  useEffect(() => {
    if (venueId) {
      fetchVenue();
      fetchPricing();
    }
  }, [venueId]);

  const fetchVenues = async () => {
    try {
      const response = await axios.get(`${API}/venue-owner/venues`);
      setVenues(response.data);
      if (response.data.length > 0 && !venueId) {
        const firstVenueId = response.data[0].id;
        navigate(`/dynamic-pricing?venueId=${firstVenueId}`, { replace: true });
      }
    } catch (error) {
      console.error("Error fetching venues:", error);
      toast.error("Sahalar yüklenemedi");
    }
  };

  const fetchVenue = async () => {
    try {
      const response = await axios.get(`${API}/venues/${venueId}`);
      setVenue(response.data);
    } catch (error) {
      console.error("Error fetching venue:", error);
    }
  };

  const fetchPricing = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/venue-owner/pricing/${venueId}`);
      setPricing(response.data);
      
      if (response.data.dayPricing && response.data.dayPricing.length > 0) {
        setDayPricing(response.data.dayPricing);
      } else if (venue) {
        setDayPricing([
          { day: 'Hafta İçi', basePrice: venue.pricePerHour || 0, timeSlots: [] },
          { day: 'Hafta Sonu', basePrice: venue.pricePerHour ? venue.pricePerHour * 1.2 : 0, timeSlots: [] }
        ]);
      }
    } catch (error) {
      console.error("Error fetching pricing:", error);
      toast.error("Fiyatlandırma yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBasePricing = async () => {
    try {
      await axios.put(`${API}/venue-owner/pricing/${venueId}`, {
        basePricePerHour: pricing.basePricePerHour,
        dayPricing: dayPricing,
        currency: 'TRY'
      });
      toast.success("Fiyatlandırma kaydedildi");
      fetchPricing();
    } catch (error) {
      console.error("Error saving pricing:", error);
      toast.error("Fiyatlandırma kaydedilemedi");
    }
  };

  const handleCreateCoupon = async () => {
    try {
      await axios.post(`${API}/venue-owner/pricing/${venueId}/coupons`, newCoupon);
      toast.success("İndirim kuponu oluşturuldu");
      setShowCouponDialog(false);
      fetchPricing();
      setNewCoupon({
        code: '',
        discountType: 'percentage',
        discountValue: 0,
        minBookingAmount: 0,
        validFrom: new Date().toISOString().split('T')[0],
        validUntil: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0],
        usageLimit: null
      });
    } catch (error) {
      console.error("Error creating coupon:", error);
      toast.error("Kupon oluşturulamadı");
    }
  };

  const handleCreatePackage = async () => {
    try {
      await axios.post(`${API}/venue-owner/pricing/${venueId}/packages`, newPackage);
      toast.success("Paket oluşturuldu");
      setShowPackageDialog(false);
      fetchPricing();
      setNewPackage({
        name: '',
        description: '',
        bookingCount: 10,
        totalPrice: 0,
        validityDays: 90
      });
    } catch (error) {
      console.error("Error creating package:", error);
      toast.error("Paket oluşturulamadı");
    }
  };

  const addTimeSlot = (dayIndex) => {
    const updated = [...dayPricing];
    updated[dayIndex].timeSlots.push({
      startTime: '18:00',
      endTime: '22:00',
      priceMultiplier: 1.5,
      label: 'Prime Time'
    });
    setDayPricing(updated);
  };

  const removeTimeSlot = (dayIndex, slotIndex) => {
    const updated = [...dayPricing];
    updated[dayIndex].timeSlots.splice(slotIndex, 1);
    setDayPricing(updated);
  };

  const updateTimeSlot = (dayIndex, slotIndex, field, value) => {
    const updated = [...dayPricing];
    updated[dayIndex].timeSlots[slotIndex][field] = value;
    setDayPricing(updated);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50 dark:bg-dark-bg dark:from-dark-bg dark:to-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
                <DollarSign className="h-8 w-8 text-emerald-600" />
                Dinamik Fiyatlandırma
              </h1>
              {venue && <p className="text-gray-600 mt-1">{venue.name}</p>}
            </div>

            {venues.length > 1 && (
              <Select value={venueId || ''} onValueChange={(value) => navigate(`?venueId=${value}`)}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue placeholder="Saha seçin" />
                </SelectTrigger>
                <SelectContent>
                  {venues.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        </div>

        <Tabs defaultValue="base" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="base">Temel Fiyat</TabsTrigger>
            <TabsTrigger value="time">Saat Bazlı</TabsTrigger>
            <TabsTrigger value="coupons">İndirim Kuponları</TabsTrigger>
            <TabsTrigger value="packages">Paketler</TabsTrigger>
          </TabsList>

          {/* Base Pricing */}
          <TabsContent value="base">
            <Card>
              <CardHeader>
                <CardTitle>Temel Fiyatlandırma</CardTitle>
                <CardDescription>Sahanız için genel fiyat ayarları</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label>Temel Saat Fiyatı (TL)</Label>
                  <Input
                    type="number"
                    value={pricing?.basePricePerHour || 0}
                    onChange={(e) => setPricing({...pricing, basePricePerHour: parseFloat(e.target.value)})}
                  />
                </div>

                {dayPricing.map((day, dayIdx) => (
                  <Card key={dayIdx}>
                    <CardHeader>
                      <CardTitle className="text-lg">{day.day}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <Label>Temel Fiyat (TL/Saat)</Label>
                        <Input
                          type="number"
                          value={day.basePrice}
                          onChange={(e) => {
                            const updated = [...dayPricing];
                            updated[dayIdx].basePrice = parseFloat(e.target.value);
                            setDayPricing(updated);
                          }}
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <Label>Saat Dilimleri</Label>
                          <Button size="sm" onClick={() => addTimeSlot(dayIdx)}>
                            <Plus className="h-4 w-4 mr-2" />
                            Dilim Ekle
                          </Button>
                        </div>

                        {day.timeSlots.map((slot, slotIdx) => (
                          <Card key={slotIdx} className="p-4 mb-2">
                            <div className="grid grid-cols-12 gap-2 items-end">
                              <div className="col-span-3">
                                <Label className="text-xs">Başlangıç</Label>
                                <Input
                                  type="time"
                                  value={slot.startTime}
                                  onChange={(e) => updateTimeSlot(dayIdx, slotIdx, 'startTime', e.target.value)}
                                />
                              </div>
                              <div className="col-span-3">
                                <Label className="text-xs">Bitiş</Label>
                                <Input
                                  type="time"
                                  value={slot.endTime}
                                  onChange={(e) => updateTimeSlot(dayIdx, slotIdx, 'endTime', e.target.value)}
                                />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs">Çarpan</Label>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={slot.priceMultiplier}
                                  onChange={(e) => updateTimeSlot(dayIdx, slotIdx, 'priceMultiplier', parseFloat(e.target.value))}
                                />
                              </div>
                              <div className="col-span-3">
                                <Label className="text-xs">Etiket</Label>
                                <Input
                                  value={slot.label}
                                  onChange={(e) => updateTimeSlot(dayIdx, slotIdx, 'label', e.target.value)}
                                  placeholder="Prime Time"
                                />
                              </div>
                              <div className="col-span-1">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => removeTimeSlot(dayIdx, slotIdx)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              Fiyat: {(day.basePrice * slot.priceMultiplier).toFixed(2)} TL/saat
                            </div>
                          </Card>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                <Button onClick={handleSaveBasePricing} className="w-full">
                  Fiyatlandırmayı Kaydet
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Time-based Pricing Summary */}
          <TabsContent value="time">
            <Card>
              <CardHeader>
                <CardTitle>Saat Bazlı Fiyat Özeti</CardTitle>
                <CardDescription>Farklı saatlerdeki fiyat değişiklikleri</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {dayPricing.map((day, idx) => (
                    <div key={idx}>
                      <h3 className="font-semibold text-lg mb-2">{day.day}</h3>
                      <div className="grid grid-cols-4 gap-2">
                        <div className="col-span-4 grid grid-cols-4 gap-2 font-semibold text-sm text-gray-600 pb-2 border-b">
                          <div>Saat Aralığı</div>
                          <div>Etiket</div>
                          <div>Çarpan</div>
                          <div>Fiyat</div>
                        </div>
                        {day.timeSlots.map((slot, sIdx) => (
                          <div key={sIdx} className="col-span-4 grid grid-cols-4 gap-2 text-sm py-2 border-b">
                            <div>{slot.startTime} - {slot.endTime}</div>
                            <div><Badge variant="outline">{slot.label}</Badge></div>
                            <div>x{slot.priceMultiplier}</div>
                            <div className="font-semibold text-emerald-600">
                              {(day.basePrice * slot.priceMultiplier).toFixed(2)} TL
                            </div>
                          </div>
                        ))}
                        {day.timeSlots.length === 0 && (
                          <div className="col-span-4 text-sm text-gray-500 py-2">
                            Özel saat dilimi yok - Tüm saatler temel fiyattan: {day.basePrice} TL
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Coupons */}
          <TabsContent value="coupons">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>İndirim Kuponları</CardTitle>
                    <CardDescription>Müşterileriniz için indirim kuponları oluşturun</CardDescription>
                  </div>
                  <Button onClick={() => setShowCouponDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Kupon
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {pricing?.coupons && pricing.coupons.length > 0 ? (
                  <div className="space-y-2">
                    {pricing.coupons.map((coupon, idx) => (
                      <Card key={idx} className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <Badge className="text-lg font-mono">{coupon.code}</Badge>
                              {coupon.isActive ? (
                                <Badge variant="outline" className="bg-green-50 text-green-700">Aktif</Badge>
                              ) : (
                                <Badge variant="outline" className="bg-gray-50 text-gray-700">Pasif</Badge>
                              )}
                            </div>
                            <div className="mt-2 text-sm text-gray-600">
                              {coupon.discountType === 'percentage' ? (
                                <span>%{coupon.discountValue} indirim</span>
                              ) : (
                                <span>{coupon.discountValue} TL indirim</span>
                              )}
                              {coupon.minBookingAmount > 0 && (
                                <span> (Min: {coupon.minBookingAmount} TL)</span>
                              )}
                            </div>
                            <div className="mt-1 text-xs text-gray-500">
                              Geçerlilik: {new Date(coupon.validFrom).toLocaleDateString('tr-TR')} - {new Date(coupon.validUntil).toLocaleDateString('tr-TR')}
                              {coupon.usageLimit && (
                                <span> | Kullanım: {coupon.usageCount}/{coupon.usageLimit}</span>
                              )}
                            </div>
                          </div>
                          <Button variant="outline" size="sm">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Henüz kupon oluşturulmamış
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Packages */}
          <TabsContent value="packages">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Paket Anlaşmalar</CardTitle>
                    <CardDescription>Toplu rezervasyon paketleri</CardDescription>
                  </div>
                  <Button onClick={() => setShowPackageDialog(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    Yeni Paket
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {pricing?.packages && pricing.packages.length > 0 ? (
                  <div className="grid md:grid-cols-2 gap-4">
                    {pricing.packages.map((pkg, idx) => (
                      <Card key={idx} className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h3 className="font-semibold text-lg">{pkg.name}</h3>
                            <p className="text-sm text-gray-600 mt-1">{pkg.description}</p>
                            <div className="mt-3 space-y-1">
                              <div className="text-sm">
                                <span className="text-gray-600">Rezervasyon Sayısı:</span>
                                <span className="font-semibold ml-2">{pkg.bookingCount} maç</span>
                              </div>
                              <div className="text-sm">
                                <span className="text-gray-600">Geçerlilik:</span>
                                <span className="font-semibold ml-2">{pkg.validityDays} gün</span>
                              </div>
                              <div className="text-lg font-bold text-emerald-600 mt-2">
                                {pkg.totalPrice} TL
                              </div>
                              <div className="text-xs text-gray-500">
                                Maç başına: {(pkg.totalPrice / pkg.bookingCount).toFixed(2)} TL
                              </div>
                            </div>
                          </div>
                          <Button variant="outline" size="sm">
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    Henüz paket oluşturulmamış
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Coupon Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni İndirim Kuponu</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Kupon Kodu</Label>
              <Input
                value={newCoupon.code}
                onChange={(e) => setNewCoupon({...newCoupon, code: e.target.value.toUpperCase()})}
                placeholder="YILBASI2024"
              />
            </div>

            <div>
              <Label>İndirim Tipi</Label>
              <Select value={newCoupon.discountType} onValueChange={(value) => setNewCoupon({...newCoupon, discountType: value})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="percentage">Yüzde (%)</SelectItem>
                  <SelectItem value="fixed">Sabit Tutar (TL)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>İndirim Değeri</Label>
              <Input
                type="number"
                value={newCoupon.discountValue}
                onChange={(e) => setNewCoupon({...newCoupon, discountValue: parseFloat(e.target.value)})}
                placeholder={newCoupon.discountType === 'percentage' ? '20' : '50'}
              />
            </div>

            <div>
              <Label>Minimum Tutar (TL)</Label>
              <Input
                type="number"
                value={newCoupon.minBookingAmount}
                onChange={(e) => setNewCoupon({...newCoupon, minBookingAmount: parseFloat(e.target.value)})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Başlangıç Tarihi</Label>
                <Input
                  type="date"
                  value={newCoupon.validFrom}
                  onChange={(e) => setNewCoupon({...newCoupon, validFrom: e.target.value})}
                />
              </div>
              <div>
                <Label>Bitiş Tarihi</Label>
                <Input
                  type="date"
                  value={newCoupon.validUntil}
                  onChange={(e) => setNewCoupon({...newCoupon, validUntil: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Kullanım Limiti (Opsiyonel)</Label>
              <Input
                type="number"
                value={newCoupon.usageLimit || ''}
                onChange={(e) => setNewCoupon({...newCoupon, usageLimit: e.target.value ? parseInt(e.target.value) : null})}
                placeholder="Sınırsız"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCouponDialog(false)}>İptal</Button>
            <Button onClick={handleCreateCoupon}>Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Package Dialog */}
      <Dialog open={showPackageDialog} onOpenChange={setShowPackageDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Yeni Paket Anlaşma</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Paket Adı</Label>
              <Input
                value={newPackage.name}
                onChange={(e) => setNewPackage({...newPackage, name: e.target.value})}
                placeholder="10 Maç Paketi"
              />
            </div>

            <div>
              <Label>Açıklama</Label>
              <Input
                value={newPackage.description}
                onChange={(e) => setNewPackage({...newPackage, description: e.target.value})}
                placeholder="10 maç rezervasyonu avantajlı fiyatla"
              />
            </div>

            <div>
              <Label>Rezervasyon Sayısı</Label>
              <Input
                type="number"
                value={newPackage.bookingCount}
                onChange={(e) => setNewPackage({...newPackage, bookingCount: parseInt(e.target.value)})}
              />
            </div>

            <div>
              <Label>Toplam Fiyat (TL)</Label>
              <Input
                type="number"
                value={newPackage.totalPrice}
                onChange={(e) => setNewPackage({...newPackage, totalPrice: parseFloat(e.target.value)})}
              />
              {newPackage.bookingCount > 0 && newPackage.totalPrice > 0 && (
                <p className="text-sm text-gray-600 mt-1">
                  Maç başına: {(newPackage.totalPrice / newPackage.bookingCount).toFixed(2)} TL
                </p>
              )}
            </div>

            <div>
              <Label>Geçerlilik Süresi (Gün)</Label>
              <Input
                type="number"
                value={newPackage.validityDays}
                onChange={(e) => setNewPackage({...newPackage, validityDays: parseInt(e.target.value)})}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPackageDialog(false)}>İptal</Button>
            <Button onClick={handleCreatePackage}>Oluştur</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DynamicPricing;
